import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TUser } from "./user";
import { useApi } from "./ApiProvider";
import { useThemesContext } from "./ThemesProvider";
import { useLanguage } from "./LanguageProvider";

type TUserContext = {
  user: TUser | null | undefined;
  setUser: (user: TUser | null | undefined) => void;
  login: (
    username: string,
    password: string
  ) => Promise<"ok" | "fail" | "error">;
  logout: () => Promise<void>;
};

const UserContext = createContext<TUserContext | null>(null);

export default function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<TUser | null | undefined>();
  const api = useApi();
  const { resetTheme, switchTheme } = useThemesContext();
  const { switchLanguage } = useLanguage();

  useEffect(() => {
    (async () => {
      if (api.isAuthenticated()) {
        const response = await api.get<TUser>("/me");
        setUser(response.ok ? (response.body as TUser) : null);
      } else {
        setUser(null);
      }
    })();
  }, [api]);

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await api.login(username, password);
      if (result === "ok") {
        const response = await api.get<TUser>("/me");
        const newUser = response.ok ? (response.body as TUser) : null;
        setUser(newUser);
        if (newUser) {
          switchTheme(newUser.theme);
          switchLanguage(newUser.language);
        }
      }
      return result;
    },
    [api, switchTheme, switchLanguage]
  );

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    resetTheme();
  }, [api, resetTheme]);

  return (
    <UserContext.Provider value={{ user, setUser, login, logout }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext) as TUserContext;
}
